import { useMemo, useEffect } from 'react';
import { User } from 'lucide-react';
import { MultipleChoiceFilter } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilter';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { iconSize } from '@duke-hq/ui/constants/scalable';

function filterValidConsultants(consultants: { id: string }[], selected?: string[]): string[] {
  if (!selected) {
    return [];
  }
  const validIds = new Set(consultants.map(({ id }) => id));
  return selected.filter((id) => validIds.has(id));
}

export default function ConsultantFilter() {
  const { updateFilters, filters } = useFiltersContext();

  const { data: allConsultants } = trpcAdmin.providers.user.listConsultants.useQuery(
    {
      providerId: filters.provider ?? '',
    },
    { enabled: filters.provider != null },
  );

  const consultants = useMemo(() => {
    return allConsultants ?? [];
  }, [, allConsultants]);

  const options = useMemo(() => {
    return consultants
      .map((consultant) => ({
        id: consultant.id,
        value: consultant.id,
        label: `${consultant.first_name} ${consultant.last_name}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [consultants]);

  const selected = useMemo(() => {
    return filterValidConsultants(consultants, filters.consultants);
  }, [consultants, filters.consultants]);

  useEffect(() => {
    if (consultants.length > 0) {
      const filteredConsultants = filterValidConsultants(consultants, filters.consultants);
      const currentConsultants = filters.consultants || [];

      // Only call updateFilters if the filtered consultants are actually different
      if (
        JSON.stringify(currentConsultants.sort()) !== JSON.stringify(filteredConsultants.sort())
      ) {
        updateFilters({ consultants: filteredConsultants });
      }
    }
  }, [filters.consultants, consultants, updateFilters]);

  if (options.length <= 1) {
    return null;
  }

  return (
    <MultipleChoiceFilter
      items={options}
      label="Consultant"
      name="consultants"
      onFilterChange={({ consultants }) => updateFilters({ consultants })}
      onlyUpdateOnChange
      value={selected.join(',')}
      variant="ghost"
      leftIcon={<User size={iconSize.md} />}
      rightIcon={undefined}
    />
  );
}
